import { auth } from './firebase';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

/**
 * Make authenticated API request
 */
async function fetchWithAuth(endpoint, options = {}) {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Not authenticated');
  }

  const token = await user.getIdToken();

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    // Backend error responses come in two shapes:
    //   { success: false, error: 'string message' }                 (most routes)
    //   { success: false, error: { code, message } }                (some timeout/structured errors)
    // Either way, surface a usable string instead of "[object Object]".
    let message;
    if (typeof body.error === 'string') {
      message = body.error;
    } else if (body.error && typeof body.error === 'object') {
      message = body.error.message || body.error.code || JSON.stringify(body.error);
    } else if (typeof body.message === 'string') {
      message = body.message;
    } else {
      message = `API error: ${response.status}`;
    }
    throw new Error(message);
  }

  return response.json();
}

export const api = {
  // Health metrics
  getDailyHealth: () => fetchWithAuth('/api/admin/metrics/daily-health'),

  // Engagement metrics
  getEngagement: () => fetchWithAuth('/api/admin/metrics/engagement'),

  // Learning metrics
  getLearning: () => fetchWithAuth('/api/admin/metrics/learning'),

  // Content metrics
  getContent: () => fetchWithAuth('/api/admin/metrics/content'),

  // Users
  getUsers: (params = {}) => {
    const searchParams = new URLSearchParams(params);
    return fetchWithAuth(`/api/admin/users?${searchParams}`);
  },

  getUserDetails: (userId) => fetchWithAuth(`/api/admin/users/${userId}`),
  getTutorConversations: (userId) => fetchWithAuth(`/api/admin/users/${userId}/tutor-conversations`),

  // Admin v2 — DO-NOW queues (trials expiring, call list, honest errors)
  getQueues: () => fetchWithAuth('/api/admin/queues'),

  // Admin v2 — Overview period metrics
  getOverview: (period) => fetchWithAuth(`/api/admin/overview?period=${encodeURIComponent(period)}`),

  // Admin v2 — AI usage & spend (Revenue page)
  getAiSpend: () => fetchWithAuth('/api/admin/ai-spend'),

  // Per-question detail for one daily quiz or chapter-practice session.
  // type ∈ 'daily_quiz' | 'chapter_practice'
  getActivityQuestions: (userId, type, activityId) =>
    fetchWithAuth(`/api/admin/users/${userId}/activity/${type}/${encodeURIComponent(activityId)}/questions`),

  // Cognitive Mastery analytics
  getCognitiveMastery: () => fetchWithAuth('/api/admin/metrics/cognitive-mastery'),

  // New metrics endpoints (Sprint 2)
  getFunnel: () => fetchWithAuth('/api/admin/metrics/funnel'),
  getActivation: () => fetchWithAuth('/api/admin/metrics/activation'),
  getAtlasMetrics: () => fetchWithAuth('/api/admin/metrics/atlas'),
  getFocusLearningMetrics: () => fetchWithAuth('/api/admin/metrics/focus-learning'),
  getRevenue: () => fetchWithAuth('/api/admin/metrics/revenue'),
  getRetentionCohorts: () => fetchWithAuth('/api/admin/metrics/retention'),
  getDeletionFunnel: (days) => fetchWithAuth(`/api/admin/metrics/deletion-funnel${days ? `?days=${days}` : ''}`),
  getEntryFunnel: (days) => fetchWithAuth(`/api/admin/metrics/entry-funnel${days ? `?days=${days}` : ''}`),
  getPaymentFailures: (days) => fetchWithAuth(`/api/admin/payments/failures${days ? `?days=${days}` : ''}`),

  // Alerts
  getAlerts: () => fetchWithAuth('/api/admin/alerts'),

  // Teachers
  getTeachers: (params = {}) => {
    const searchParams = new URLSearchParams(params);
    return fetchWithAuth(`/api/teachers?${searchParams}`);
  },

  getTeacher: (teacherId) => fetchWithAuth(`/api/teachers/${teacherId}`),

  createTeacher: (teacherData) => fetchWithAuth('/api/teachers', {
    method: 'POST',
    body: JSON.stringify(teacherData),
  }),

  updateTeacher: (teacherId, updates) => fetchWithAuth(`/api/teachers/${teacherId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  }),

  deactivateTeacher: (teacherId) => fetchWithAuth(`/api/teachers/${teacherId}`, {
    method: 'DELETE',
  }),

  // Teacher Portal: profile lookup (used by useAuth to detect teacher role)
  getTeacherProfile: () => fetchWithAuth('/api/teachers/me'),

  // ----- Teacher Dashboard v2 — Coaching Centers (admin) -----
  getCoachingCenters: (params = {}) => {
    const sp = new URLSearchParams(params);
    return fetchWithAuth(`/api/admin/coaching-centers?${sp}`);
  },
  getCoachingCenter: (centerId) => fetchWithAuth(`/api/admin/coaching-centers/${centerId}`),
  createCoachingCenter: (data) => fetchWithAuth('/api/admin/coaching-centers', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  updateCoachingCenter: (centerId, updates) => fetchWithAuth(`/api/admin/coaching-centers/${centerId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  }),
  deactivateCoachingCenter: (centerId) => fetchWithAuth(`/api/admin/coaching-centers/${centerId}`, {
    method: 'DELETE',
  }),

  // ----- Teacher Dashboard v2 — Batches (admin) -----
  getBatches: (params = {}) => {
    const sp = new URLSearchParams(params);
    return fetchWithAuth(`/api/admin/batches?${sp}`);
  },
  getBatch: (batchId) => fetchWithAuth(`/api/admin/batches/${batchId}`),
  createBatch: (data) => fetchWithAuth('/api/admin/batches', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  updateBatch: (batchId, updates) => fetchWithAuth(`/api/admin/batches/${batchId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  }),
  addTeachersToBatch: (batchId, teacherIds) => fetchWithAuth(`/api/admin/batches/${batchId}/teachers`, {
    method: 'POST',
    body: JSON.stringify({ teacher_ids: teacherIds }),
  }),
  removeTeacherFromBatch: (batchId, teacherId) => fetchWithAuth(`/api/admin/batches/${batchId}/teachers/${teacherId}`, {
    method: 'DELETE',
  }),
  addStudentsToBatch: (batchId, studentIds) => fetchWithAuth(`/api/admin/batches/${batchId}/students`, {
    method: 'POST',
    body: JSON.stringify({ student_ids: studentIds }),
  }),
  removeStudentFromBatch: (batchId, studentId) => fetchWithAuth(`/api/admin/batches/${batchId}/students/${studentId}`, {
    method: 'DELETE',
  }),
  transferStudent: (fromBatchId, studentId, toBatchId) => fetchWithAuth(`/api/admin/batches/${fromBatchId}/students/${studentId}/transfer`, {
    method: 'POST',
    body: JSON.stringify({ to_batch_id: toBatchId }),
  }),

  // ----- Teacher Dashboard v2 — Teacher subjects -----
  updateTeacherSubjects: (teacherId, subjects) => fetchWithAuth(`/api/teachers/${teacherId}/subjects`, {
    method: 'PATCH',
    body: JSON.stringify({ subjects }),
  }),

  // ----- Teacher Dashboard v2 — Teacher portal read endpoints -----
  // All under /api/teachers/me/... — accept both teacher and admin tokens
  // (admin tokens go through authenticateTeacherOrAdmin → view-as-teacher).
  getTeacherContexts: () => fetchWithAuth('/api/teachers/me/contexts'),
  getTeacherDashboardV2: (batchId, subject) =>
    fetchWithAuth(`/api/teachers/me/batches/${batchId}/${subject}/dashboard`),
  getTeacherClassGapsV2: (batchId, subject) =>
    fetchWithAuth(`/api/teachers/me/batches/${batchId}/${subject}/class-gaps`),
  getTeacherParentDataV2: (batchId, subject) =>
    fetchWithAuth(`/api/teachers/me/batches/${batchId}/${subject}/parent-data`),
  getTeacherStudentDrillDownV2: (batchId, subject, studentId) =>
    fetchWithAuth(`/api/teachers/me/batches/${batchId}/${subject}/students/${studentId}`),
};

export default api;
